/**
 * GraphQL-backed LlmClient.
 *
 * Implements the @astrana/ui clinical `LlmClient` interface by proxying chat
 * completions through care-management-gql's `llmChatCompletion` mutation. The
 * Azure OpenAI API key lives on the server — it is NEVER shipped in this
 * browser bundle. This replaces the previous `azureLlmClient.ts`, which read
 * `VITE_AZURE_OPENAI_API_KEY` from `import.meta.env` (a security smell).
 *
 * The clinical library still owns the CBP prompt and the zod cross-check; it
 * builds the `messages` and we simply forward them to the server.
 */
import { ApolloClient, gql } from "@apollo/client";
import type { LlmClient } from "@astrana/ui/clinical";

export const LLM_CHAT_COMPLETION = gql`
  mutation LlmChatCompletion(
    $messages: [ChatMessageInput!]!
    $temperature: Float
    $maxTokens: Int
    $jsonResponse: Boolean
  ) {
    llmChatCompletion(
      messages: $messages
      temperature: $temperature
      maxTokens: $maxTokens
      jsonResponse: $jsonResponse
    )
  }
`;

/**
 * Builds an LlmClient backed by the care-management-gql proxy.
 *
 * @param apolloClient the app's Apollo client (already configured with the
 *   Entra auth link). No PP token is needed — the LLM proxy is gated by the
 *   normal Entra session, so this works under SSO-only deployments.
 */
export function createGqlLlmClient(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apolloClient: ApolloClient<any>,
): LlmClient {
  return {
    async chatCompletion(req) {
      const { data } = await apolloClient.mutate<{
        llmChatCompletion: string;
      }>({
        mutation: LLM_CHAT_COMPLETION,
        variables: {
          messages: req.messages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          temperature: req.temperature,
          maxTokens: req.maxTokens,
          jsonResponse: req.jsonResponse,
        },
        // Forward the library's AbortSignal so timeouts/cancellation work.
        context: req.signal
          ? { fetchOptions: { signal: req.signal } }
          : undefined,
        fetchPolicy: "no-cache",
      });

      const content = data?.llmChatCompletion;
      if (typeof content !== "string") {
        throw new Error("llmChatCompletion returned no content.");
      }
      return content;
    },
  };
}
